/* eslint-disable prettier/prettier */
import React from 'react';
import { Text, BadgeRounded, Spacing, ProviderLogo } from '@reservamos/elements';
import PropTypes from 'prop-types';
import './DiscountBanner.scss';
import { useTranslation } from 'react-i18next';
import useSearch from 'hooks/store/useSearch';
import { useSelector } from 'react-redux';
import useWhitelabelFeatures from 'hooks/whitelabel/useWhitelabelFeatures';
import { normalizeAmenities } from 'utils/trips/parseTripDetails';
import Amenity from './Amenity';

/**
 * Renders the DiscountBanner component.
 *
 * @param {Object} props - The component props.
 * @param {string} props.discountPercent - The discount percentage.
 * @param {string} props.way - The direction of the trip ('departure' or 'return').
 * @returns {JSX.Element} The rendered component.
 */
const DiscountBanner = ({ discountPercent, way }) => {
  const { t } = useTranslation('common');
  const { SHOW_DIGITAL_DISCOUNT_BANNER, SHOW_DEFAULT_AMENITIES } = useWhitelabelFeatures();
  const { departure: searchDeparture, return: searchReturn } = useSearch();
  const lines = useSelector((state) => state.lines);
  const { routeType } = searchDeparture;
  const isBulkTicket = routeType === 'bulk_ticket';
  const searchId = way === 'departure' ? searchDeparture.id : searchReturn?.id;
  const trips = useSelector((state) => state.trips.toJS());
  const tripList = [...trips[searchId].buses.trips];

  /**
   * Gets the shared amenities between all trips
   * @returns {Array<string>} Array of normalized amenities
   */
  const getSharedAmenities = () => {
    if (!tripList?.length) return [];

    // Normalize amenities for each trip
    const normalizedAmenitiesList = tripList.map((trip) =>
      normalizeAmenities(trip?.amenities || []),);

    // Get all unique amenities
    const uniqueAmenities = [...new Set(normalizedAmenitiesList.flat())];

    // Filter only amenities that are present in all trips
    return uniqueAmenities.filter((amenity) =>
      normalizedAmenitiesList.some((tripAmenities) => tripAmenities.includes(amenity)),);
  };

  const sharedAmenities = getSharedAmenities();

  if (routeType === 'default') return null;

  return (
    <div className="banner-discount">
      {SHOW_DIGITAL_DISCOUNT_BANNER && (
        <Spacing size="XS" alignItems="center">
          <BadgeRounded
            equalRadius
            size="M"
            text={discountPercent}
            firstColor="discountLight"
            secondColor="discountStrong"
          />
          <Text mobileSize="XXS" size="S" color="grayStrong" italic>
            {t('common:labels.digital_purchase')}
          </Text>
        </Spacing>
      )}
      <Spacing size="XS" alignItems="center">
        {isBulkTicket && (
          <Spacing size="S" alignItems="center">
            <Text color="grayLight">{t('common:labels.provider')}:</Text>
            {Object.values(lines).map(
              (line) =>
                line.logoUrl && <ProviderLogo imgSrc={line.logoUrl} name={line.abbr} isSmall />,
            )}
          </Spacing>
        )}
        {!SHOW_DIGITAL_DISCOUNT_BANNER && !isBulkTicket && (
          <Text size="XS">{t('general:amenities')}</Text>
        )}
        {!SHOW_DEFAULT_AMENITIES && sharedAmenities.length > 0 && (
          <>
            <Text color="grayLight"> | </Text>
            {sharedAmenities.map((amenity) => (
              <Amenity key={amenity} type={amenity} isIcon iconSize="S" />
            ))}
          </>
        )}
        {SHOW_DEFAULT_AMENITIES && (
          <>
            <Text color="grayLight"> | </Text>
            <Amenity type="bathroom" isIcon iconSize="S" />
            <Amenity type="tv" isIcon iconSize="S" />
            <Amenity type="wifi" isIcon iconSize="S" />
          </>
        )}
      </Spacing>
    </div>
  );
};

DiscountBanner.propTypes = {
  discountPercent: PropTypes.string,
  way: PropTypes.string,
};

export default DiscountBanner;
